import React, { useState, useEffect } from "react";
import { ModuleAccordionSection, MaterialIcon, ButtonText } from "@loanbossinc/component-library";
import { IconButton } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import PreviewIcon from "@material-ui/icons/Visibility";
import LockIcon from "@material-ui/icons/Lock";
import moment from "moment";
import numeral from "numeral";
import _ from "lodash";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ToastError } from "components/Global/Toasts/Toasty";
import { authorizedRequest } from "utils/api";

import {
  StyledReportHeaderWrapper,
  StyledReportHeaderDates,
  StyledAsOfDate,
  StyledDueDate,
  StyledHeaderStats,
  StyledHeaderInfo,
  StyledReportComponentDate,
  StyledReportComponentName,
  StyledHeaderLinks,
  StyledReportComponentHeaderWrapper,
  StyledRefreshButton,
  RefreshWrapper,
  LockedIconStyle,
  StyledIconButton
} from "./styles";

const LockedSwitch = ({ locked, isSubmitted, isGuest, toggleLock, isDraft }) => (
  <LockedIconStyle isLocked={locked || isSubmitted} onClick={isSubmitted ? undefined : toggleLock} style={{display: isDraft ? "none" : undefined }}>
    <IconButton disabled={isGuest || isSubmitted} aria-label="Locked" size="small">
      {locked || isSubmitted ? <LockIcon /> : <MaterialIcon iconName="lock_open_outline" />}
    </IconButton>
  </LockedIconStyle>
);

const dateDisplayFormat = "MMMM DD, YYYY";

const ReportHeader = props => {
  const { isLocked, isDraft, asOfDate, dueDate, isGuest, handleRecalculation, toggleLock, id, isSystemAdmin, setDashboardSelections, isSubmitted } = props;

  const onSelect = () => {
    setDashboardSelections({ selectedRgiIdForLogs: id });
  };

  return (
    <StyledReportHeaderWrapper isDraft={isDraft}>
      <StyledReportHeaderDates>
        <StyledAsOfDate>
          {`As of ${moment.utc(asOfDate).format(dateDisplayFormat)}`}
        </StyledAsOfDate>
        <StyledDueDate style={{ display: isDraft ? "none" : undefined }}>
          {`Due on ${moment.utc(dueDate).format(dateDisplayFormat)}`}
        </StyledDueDate>
      </StyledReportHeaderDates>
      <RefreshWrapper>
        {isSystemAdmin && <ButtonText label="Calculation Logs" style={{ marginRight: "0.5em" }} onClick={onSelect} />}
        <StyledRefreshButton
          id="refresh-button"
          label="Refresh calculation"
          iconName="cached"
          reverse
          isLocked={isLocked}
          disabled={isGuest}
          onClick={handleRecalculation}
          style={{display: isLocked ? "none" : undefined}}
        />
        <LockedSwitch locked={isLocked} isSubmitted={isSubmitted} isGuest={isGuest} toggleLock={toggleLock} id={id} isDraft={isDraft} />
      </RefreshWrapper>
    </StyledReportHeaderWrapper>
  );
};

const ReportInstanceTypeRow = ({ repInstance, previewReport, updatedAt, id, dealName, dealId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { type, total } = repInstance;

  const onReportDownload = async () => {
    setIsDownloading(true);

    const response = await authorizedRequest(`/report-groups/instances/createExcelDocuments/${id}/${type}/${dealId}/${encodeURIComponent(dealName)}`, "GET", null, null, true);

    if (response.url) {
      const link = document.createElement("a");
      link.href = response.url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      ToastError("Unable to generate the report.");
    }

    setIsDownloading(false);
  };

  const convertTypeToString = () => {
    if (type === "DSCR") {
      return "DSCR";
    }
    if (type === "DebtYield") {
      return "Debt Yield";
    }

    return "";
  };

  let convertedValue;
  if (!total && total !== 0) {
    convertedValue = "--";
  } else if (type === "DSCR") {
    convertedValue = `${numeral(total).format("0.00", Math.round)}x`;
  } else if (type === "DebtYield") {
    convertedValue = numeral(total).format("0.00%", Math.round);
  }

  return (
    <ModuleAccordionSection
      isCustomSummary
      customContent={
        <StyledReportComponentHeaderWrapper>
          <div style={{ display: "flex", width: "100%", justifyContent: "flex-start" }}>
            <StyledHeaderStats>{convertedValue}</StyledHeaderStats>
            <StyledHeaderInfo>
              <StyledReportComponentName>{convertTypeToString()}</StyledReportComponentName>
              <StyledReportComponentDate>{`Last calculated on ${moment.utc(updatedAt).format(dateDisplayFormat)}`}</StyledReportComponentDate>
            </StyledHeaderInfo>
            <StyledHeaderLinks>
              {isDownloading ? (
                <div style={{ width: "18px", height: "18px", alignSelf: "center", marginRight: "4px" }}>
                  <CircularProgress style={{ color: "#0f9ad6", width: "18px", height: "18px" }} />
                </div>
              ) : (
                <StyledIconButton disabled={convertedValue === "--"}>
                  <GetAppIcon onClick={onReportDownload} />
                </StyledIconButton>
              )}
              <StyledIconButton disabled={convertedValue === "--"}>
                <PreviewIcon onClick={() => previewReport && previewReport(type, id)} />
              </StyledIconButton>
            </StyledHeaderLinks>
          </div>
        </StyledReportComponentHeaderWrapper>
      }
    />
  );
};

const ReportGroupInstance = props => {
  const { previewReport, isGuest, reportId, recalculateRgi, toggleLockRgi, rgi, dealName, dealId, isSystemAdmin, setDashboardSelections, displayRows } = props;
  const { id, isLocked, isDraft, asOfDate, dueDate, reportInstances, updatedAt, isProjected, isSubmitted } = rgi;

  const handleRecalculation = () => {
    recalculateRgi({ reportGroupId: reportId, asOfDate, rgiType: getRgiType() });
  };

const getRgiType = () => {
  if(!isDraft){
    return "regular";
  }
  else if(isDraft && isProjected){
    return "projected";
  }
  else{
    return "draft";
  }
};

  const toggleLock = () => {
    const updatedRgi = {
      ...rgi,
      isLocked: !isLocked,
      isManuallyUnlocked: isLocked
    };
    toggleLockRgi({ rgi: _.omit(updatedRgi, ["updatedAt", "createdAt"]) });
  };

  return (
    <div style={{ marginBottom: "2em" }}>
      <ReportHeader
        id={id}
        isLocked={isLocked}
        isDraft={isDraft}
        asOfDate={asOfDate}
        dueDate={dueDate}
        isGuest={isGuest}
        handleRecalculation={handleRecalculation}
        toggleLock={toggleLock}
        isSystemAdmin={isSystemAdmin}
        setDashboardSelections={setDashboardSelections}
        isSubmitted={!!isSubmitted}
      />
      {displayRows && reportInstances &&
        reportInstances.map(repInst => (
          <ReportInstanceTypeRow
            repInstance={repInst}
            key={repInst.type}
            previewReport={previewReport}
            updatedAt={updatedAt}
            id={id}
            dealName={dealName}
            dealId={dealId}
          />
        ))}
    </div>
  );
};

export default ReportGroupInstance;
